import { Breadcrumb, Button, Form, Input, message, Typography } from "antd";
import { GoogleOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import { sendPasswordResetEmail } from "firebase/auth";
import { useState } from "react";
import {
  useFetcher,
  useLoaderData,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { auth } from "~/lib";
import { breadcrumbHomeItem, breadcrumbItem } from "~/components";
import { useFetcherBusy } from "~/hooks/useRouteActivity";
import { INTENTS } from "~/utils";
const { Title, Text } = Typography;

const SignUpPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true);
  const fetcher = useFetcher();
  const [form] = Form.useForm();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") || "";

  const isSubmitting = useFetcherBusy(fetcher);

  const handleFormToggle = () => {
    setIsSignUp(!isSignUp);
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    fetcher.submit(
      {
        intent: isSignUp ? INTENTS._google_signup : INTENTS._google_signin,
        returnUrl: returnUrl,
      },
      { method: "post" }
    );

    form.resetFields();
    setLoading(false);
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      fetcher.submit(
        {
          intent: isSignUp ? INTENTS._signup : INTENTS._signin,
          body: JSON.stringify(values),
          returnUrl: returnUrl,
        },
        { method: "post" }
      );
    } catch (error: any) {
      message.error(t("Error_submitting_form"), error);
      console.error(t("Error_submitting_form"), error);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    try {
      const email = form.getFieldValue("email");
      await sendPasswordResetEmail(auth, email || "");
      message.success(t("Password_reset_email_sent"));
    } catch (error) {
      message.error(t("Password_reset_email_failed"));
    }
  };
  return (
    <>
      <Breadcrumb
        separator=">"
        items={[breadcrumbHomeItem, breadcrumbItem("Sign-Up")]}
      />

      <Content>
        <div className="signup-form">
          <Title className="signup-form-title" level={1}>
            {isSignUp ? t("Sign_up") : t("Sign_in")}
          </Title>
          <Form form={form} onFinish={handleSubmit}>
            {!isSignUp && (
              <>
                <Form.Item
                  className="signup-form-item"
                  label={t("Email")}
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: t("Please_enter_your_email"),
                    },
                    {
                      type: "email",
                      message: t("Please_enter_a_valid_email"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  className="signup-form-item"
                  label={t("Password")}
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t("Please_enter_your_password"),
                    },
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                <Form.Item className="signup-form-forgot-password">
                  <Button type="link" onClick={handleForgotPassword}>
                    {t("Forgot_password")}
                  </Button>
                </Form.Item>
              </>
            )}
            {isSignUp && (
              <>
                <Form.Item
                  className="signup-form-item"
                  label={t("First_Name")}
                  name="firstName"
                  rules={[{ required: true, message: t("Please_enter_your_first_name") }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  className="signup-form-item"
                  label={t("Last_Name")}
                  name="lastName"
                  rules={[{ required: true, message: t("Please_enter_your_last_name") }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  className="signup-form-item"
                  label={t("Email")}
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: t("Please_enter_your_email"),
                    },
                    {
                      type: "email",
                      message: t("Please_enter_a_valid_email"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  className="signup-form-item"
                  label={t("Username")}
                  name="username"
                  rules={[
                    {
                      required: true,
                      message: t("Please_enter_your_username"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  className="signup-form-item"
                  label={t("Password")}
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: t("Please_enter_your_password"),
                    },
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              </>
            )}
            <Form.Item className="signup-form-submit-button">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={isSubmitting || loading}
                block
              >
                {isSignUp ? t("Sign_up") : t("Sign_in")}
              </Button>
            </Form.Item>
            <Form.Item className="signup-form-submit-button">
              <Button
                type="primary"
                onClick={handleGoogleLogin}
                icon={<GoogleOutlined />}
                disabled={isSubmitting || loading}
              >
                Google {isSignUp ? t("Sign_up") : t("Sign_in")}
              </Button>
            </Form.Item>
            <Form.Item className="signup-form-toggle">
              <Text>
                {isSignUp
                  ? t("Already_have_an_account")
                  : t("Dont_have_an_account")}
                <Button
                  type="link"
                  onClick={handleFormToggle}
                  disabled={isSubmitting || loading}
                >
                  {isSignUp ? t("Sign_in") : t("Sign_up")}
                </Button>
              </Text>
            </Form.Item>
          </Form>
        </div>
      </Content>
    </>
  );
};

export default SignUpPage;
